import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import {
    HasProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    CartLineModel,
    ProductSettingsModel,
    WarehouseDto,
    WarehouseModel,
} from "@insite/client-framework/Types/ApiModels";
import { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import { useLoadScript } from "@react-google-maps/api";
import FindLocationModal from "@WhiteCap/Overrides/Components/FindLocationModal";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    product?: ProductContextModel | CartLineModel;
    useLoadScript: ({ googleMapsApiKey, region }: { googleMapsApiKey: any; region?: string }) => { isLoaded: boolean };
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    cart: getCurrentCartState(state).value,
    currentPage: getCurrentPage(state).type,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    updatePickUpWarehouse,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    centeringWrapper?: InjectableCss;
    findLocationModal?: FindLocationModalStyles;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: { width: [12, 12, 6, 6, 6] },
    availabilityByWarehouseLinkGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 10px !important;
                    `,
                    css`
                        margin-top: 10px !important;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    availabilityByWarehouseLink: {
        css: css`
            font-size: 12px;
            font-family: "Gotham A", "Gotham B";
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            font-size: 12px;
            line-height: 16px;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    padding: 5px;
    border-radius: 8px;
    /* Commenting below code as US has added custom color through code injection for the border */
    /* border: 1px solid ${props => safeColor(resolveColor(props.color, props.theme)).rgb().alpha(0.2).string()}; */
    border: 1px solid ${props => props.color};
    ${IconWrapper} {
        margin-right: 5px;
        height: 10px;
        width: 13px;
    }
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        // Commenting below code as US has added custom color through code injection for the border and not from the themes
        // return "common";
        return "#F8F9FA";
    }

    const availabilityType = availability?.messageType;
    // EC-675 : Showing the inventory available color and svg for all products, so commenting below code
    // if (availabilityType === AvailabilityMessageType.OutOfStock) {
    //     return "danger";
    // }

    // Showing the inventory available color and svg for low stock and available products
    if (availabilityType === AvailabilityMessageType.LowStock || availabilityType === AvailabilityMessageType.OutOfStock) {
        // Commenting below code as US has added custom color through code injection for the border and not from the themes
        // return "warning";
        return "#ffcc33";
    }

    // Commenting below code as US has added custom color through code injection for the border and not from the themes
    // return "success";
    return "#54BCAB";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    // EC-675 : Showing the inventory available color and svg for all products, so commenting below code
    const availabilityType = availability?.messageType;
    // if (availabilityType === AvailabilityMessageType.OutOfStock) {
    //     return X;
    // }

    if (availabilityType === AvailabilityMessageType.LowStock || availabilityType === AvailabilityMessageType.OutOfStock) {
        return AlertTriangle;
    }

    return Check;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    currentPage?: string,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    if (currentPage === "ProductListPage" || currentPage === "MyListsDetailsPage") {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    productContext,
    currentPage,
    product: pricingData,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    updatePickUpWarehouse,
    cart,
}: Props) => {
    const toasterContext = React.useContext(ToasterContext);

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));
    if (!showInventory) {
        return null;
    }

    if (!cart) {
        return null;
    }

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText} data-test-selector="inventoryLoadFailedMessage">
                    {siteMessage("RealTimeInventory_InventoryLoadFailed")}
                </Typography>
            </StyledWrapper>
        );
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    /* EC-488 - Update Inventory Status Messages
    Have removed the "pricing>0" condition from below if to display inventory messages
    even if the product price is 0.
    */
    if (
        availability?.messageType !== AvailabilityMessageType.NoMessage &&
        availability?.message?.length &&
        productContext.product?.properties?.callForPrice !== "true"
    ) {
        inventoryTextComponent = (
            <InventoryMessage color={color} {...styles.inventoryMessage}>
                <Icon color={color} src={iconSrc} />
                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                    {availability.message}
                </Typography>
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    const handlePickUpAddressChange = async (pickUpWarehouse: WarehouseModel) => {
        await updatePickUpWarehouse({ pickUpWarehouse });
        toasterContext.addToast({
            body:
                cart.fulfillmentMethod === FulfillmentMethod.PickUp
                    ? translate("Pick Up Address Updated")
                    : translate("Ship Address Updated"),
            messageType: "success",
        });
    };

    const handleWarehouseSelected = (warehouse: WarehouseModel) => {
        handlePickUpAddressChange(warehouse);
        setModalIsOpen(false);
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
            {showLink(availability, trackInventory, productSettings, currentPage, isProductDetailsPage) && (
                <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                    >
                        {translate("View Availability by Branch")}
                    </Link>

                    <FindLocationModal
                        modalIsOpen={modalIsOpen}
                        onWarehouseSelected={handleWarehouseSelected}
                        onModalClose={modalCloseHandler}
                        extendedStyles={styles.findLocationModal}
                        inventoryWarehouses={warehouses}
                        isWarehouseInventoryLoading={isLoading}
                        warehouseInventoryErrorMessage={errorMessage}
                        useLoadScript={useLoadScript}
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductAvailability));
